'use client';

import { useState, useEffect, useRef } from 'react';
import React from 'react';
import { motion } from 'framer-motion';
import Joyride, { Step } from 'react-joyride';
import {
  Palette,
  QrCode,
  Download,
  Trash2,
  Copy,
  ExternalLink,
  Calendar,
  Sparkles,
  Pencil,
  Plus,
  CreditCard,
  FileImage,
  FileText,
  X,
} from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { toPng } from 'html-to-image';
import { jsPDF } from 'jspdf';
import { getTourStyles } from '@/components/tour/tourStyles';
import { Card, CardContent } from '@/components/ui/card';
import { GLASS_CARD } from '@/components/ui/glass';
import { Button } from '@/components/ui/button';
import { Switch } from '@/components/ui/switch';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '@/components/ui/dialog';

import GenerateQRTourSteps from '@/components/tour/company/GenerateQRTourSteps';
import Pagination from '@/components/pages/shared/Pagination';
import { useGenerateQR } from '@/components/hooks/useGenerateQR';
import { useEmpresa } from '@/components/hooks/useEmpresa';
import {
  useBusinessCards,
  type BusinessCard,
} from '@/components/hooks/useBusinessCards';
import BusinessCardDialog from '@/components/pages/dialogs/BusinessCardDialog';
import {
  DLG_BTN_PRIMARY,
  DLG_BTN_SECONDARY,
} from '@/components/ui/dialogButtons';

import {
  zigzagPalettes,
  ZigZagPalette,
  FrontCard,
  BackCard,
  CARD_W,
  CARD_H,
  type InvitationDesignProps,
} from '@/components/pages/company/invitationDesigns';
import { usePageHelp, useTourVisibility } from '@/components/hooks/usePageHelp';

/* ── Constantes ─────────────────────────────────────────────────── */

const PALETTE_STORAGE_KEY = 'invitation_zigzag_palette';
const PRIMARY_COLOR_KEY = 'branding_primary_color';
const SECONDARY_COLOR_KEY = 'branding_secondary_color';

// Grid de hasta 3 columnas (md:grid-cols-2 / xl:grid-cols-3): opciones
// múltiplos de 3 para que la última fila no quede coja en xl.
const DEFAULT_CARDS_PAGE_SIZE = 6;
const CARDS_PAGE_SIZE_OPTIONS = [3, 6, 9, 12];
const CARDS_PAGE_SIZE_STORAGE_KEY = 'bookoll:branding-cards:pageSize';

const readStoredCardsPageSize = () => {
  if (typeof window === 'undefined') return DEFAULT_CARDS_PAGE_SIZE;
  const stored = Number(localStorage.getItem(CARDS_PAGE_SIZE_STORAGE_KEY));
  return CARDS_PAGE_SIZE_OPTIONS.includes(stored)
    ? stored
    : DEFAULT_CARDS_PAGE_SIZE;
};

const COLOR_PALETTE = [
  '#e63950',
  '#103254',
  '#2563eb',
  '#7c3aed',
  '#10b981',
  '#f59e0b',
  '#ef4444',
  '#0ea5e9',
  '#22c55e',
  '#f97316',
  '#6366f1',
  '#14b8a6',
];

const CARD_SIZES = [
  { id: '8x5', label: '8 × 5 cm', widthMm: 80, heightMm: 50 },
  { id: '85x55', label: '8.5 × 5.5 cm', widthMm: 85, heightMm: 55 },
  { id: '9x5', label: '9 × 5 cm', widthMm: 90, heightMm: 50 },
] as const;

type CardSizeId = (typeof CARD_SIZES)[number]['id'];

/* ── Animaciones ────────────────────────────────────────────────── */

const containerVariants = {
  hidden: { opacity: 0, y: 10 },
  visible: { opacity: 1, y: 0, transition: { staggerChildren: 0.1 } },
};
const itemVariants = {
  hidden: { opacity: 0, y: 10 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.5 } },
};
const fadeUp = {
  hidden: { opacity: 0, y: 16 },
  visible: (i = 0) => ({
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.45,
      delay: i * 0.08,
      ease: [0.22, 1, 0.36, 1] as [number, number, number, number],
    },
  }),
};

const isMobile = window.innerWidth < 640;

/* ── Helpers ────────────────────────────────────────────────────── */

const getStoredColor = (key: string, fallback: string): string => {
  if (typeof window === 'undefined') return fallback;
  try {
    return localStorage.getItem(key) ?? fallback;
  } catch {
    return fallback;
  }
};
const isValidHex = (v: string) => /^#[0-9a-fA-F]{6}$/.test(v);

const waitForImages = (el: HTMLElement) =>
  Promise.all(
    Array.from(el.querySelectorAll('img')).map((img) =>
      img.complete
        ? Promise.resolve()
        : new Promise<void>((r) => {
            img.onload = () => r();
            img.onerror = () => r();
          })
    )
  );

const toDataUrl = async (url: string): Promise<string> => {
  const res = await fetch(url, { mode: 'cors' });
  const blob = await res.blob();
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result as string);
    reader.onerror = reject;
    reader.readAsDataURL(blob);
  });
};

/* ── StatusPill ─────────────────────────────────────────────────── */

const StatusPill = ({
  active,
  labelOn,
  labelOff,
}: {
  active: boolean;
  labelOn: string;
  labelOff: string;
}) => (
  <span
    className={`inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full text-xs font-medium tracking-wide ${
      active
        ? 'bg-emerald-50 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400'
        : 'bg-red-50 text-red-600 dark:bg-red-900/30 dark:text-red-400'
    }`}
  >
    <span
      className={`w-1.5 h-1.5 rounded-full ${
        active ? 'bg-emerald-500' : 'bg-red-500'
      }`}
    />
    {active ? labelOn : labelOff}
  </span>
);

/* ── SizePickerDialog ───────────────────────────────────────────── */

const SizePickerDialog = ({
  open,
  format,
  onConfirm,
  onClose,
}: {
  open: boolean;
  format: 'png' | 'pdf' | null;
  onConfirm: (id: CardSizeId) => void;
  onClose: () => void;
}) => {
  const [selected, setSelected] = useState<CardSizeId>('85x55');
  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent
        className="
          z-[60] p-0 border border-gray-200 dark:border-white/10 bg-white dark:bg-[#1c1c1e]
          [&>button:last-child]:hidden
          flex flex-col
          !bottom-0 !left-0 !top-auto !translate-x-0 !translate-y-0
          w-full max-w-none rounded-t-2xl rounded-b-none
          data-[state=open]:slide-in-from-bottom data-[state=closed]:slide-out-to-bottom
          sm:!bottom-auto sm:!left-1/2 sm:!top-1/2 sm:!-translate-x-1/2 sm:!-translate-y-1/2
          sm:w-[95%] sm:max-w-sm sm:mx-auto sm:h-auto sm:rounded-xl sm:shadow-xl
        "
      >
        <div className="px-4 sm:px-6 pt-5 pb-3 shrink-0 relative">
          <DialogHeader>
            <DialogTitle className="text-lg sm:text-xl font-semibold text-gray-900 dark:text-white pr-10">
              Tamaño de la tarjeta
            </DialogTitle>
            <DialogDescription className="text-xs text-gray-500 dark:text-gray-400">
              {format === 'pdf'
                ? 'PDF de 2 páginas (cara y dorso) con estas dimensiones exactas'
                : 'Dos PNG independientes (cara y dorso) a estas medidas aprox.'}
            </DialogDescription>
          </DialogHeader>
          <button
            onClick={onClose}
            className="absolute top-4 right-4 w-9 h-9 flex items-center justify-center rounded-full text-gray-500 hover:text-gray-800 dark:hover:text-gray-100 hover:bg-gray-100 dark:hover:bg-white/10 transition-colors"
            aria-label="Cerrar"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="px-4 sm:px-6 pb-5 flex flex-col gap-2">
          {CARD_SIZES.map((s) => (
            <button
              key={s.id}
              onClick={() => setSelected(s.id as CardSizeId)}
              className={`flex items-center gap-3 px-4 py-3 rounded-lg border text-left transition-colors ${
                selected === s.id
                  ? 'border-[#103254] bg-[#103254]/5 dark:border-blue-400 dark:bg-blue-400/10'
                  : 'border-gray-200 dark:border-white/10 hover:border-gray-300 dark:hover:border-white/20'
              }`}
            >
              <span
                className={`w-4 h-4 rounded-full border-2 flex items-center justify-center shrink-0 ${
                  selected === s.id
                    ? 'border-[#103254] dark:border-blue-400'
                    : 'border-gray-300 dark:border-white/20'
                }`}
              >
                {selected === s.id && (
                  <span className="w-2 h-2 rounded-full bg-[#103254] dark:bg-blue-400" />
                )}
              </span>
              <div>
                <p className="text-sm font-medium text-gray-900 dark:text-white">
                  {s.label}
                </p>
                <p className="text-xs text-gray-400 dark:text-gray-500">
                  {s.widthMm} × {s.heightMm} mm
                </p>
              </div>
            </button>
          ))}
        </div>

        <DialogFooter className="px-4 sm:px-6 pt-2 pb-5 sm:pb-4 flex flex-row gap-2 shrink-0 border-t border-gray-100 dark:border-white/10">
          <Button
            variant="outline"
            onClick={onClose}
            className={`flex-1 ${DLG_BTN_SECONDARY}`}
          >
            Cancelar
          </Button>
          <Button
            className={`flex-1 ${DLG_BTN_PRIMARY}`}
            onClick={() => onConfirm(selected)}
          >
            Descargar
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

/* ── CardRow ────────────────────────────────────────────────────── */

type CardRowProps = {
  card: BusinessCard;
  cardPalette: ZigZagPalette;
  empresaInfo: any;
  qrUrl: string | null;
  qrCatalogoUrl: string | null;
  reservasOn: boolean;
  isFirst: boolean;
  onEdit: () => void;
  onDelete: () => void;
};

const CardRow = ({
  card,
  cardPalette,
  empresaInfo,
  qrUrl,
  qrCatalogoUrl,
  reservasOn,
  isFirst,
  onEdit,
  onDelete,
}: CardRowProps) => {
  const frontRef = useRef<HTMLDivElement>(null);
  const backRef = useRef<HTMLDivElement>(null);

  const containerRef = useRef<HTMLDivElement>(null);
  const [scale, setScale] = useState(1);
  useEffect(() => {
    const update = () => {
      if (!containerRef.current) return;
      const avail = containerRef.current.clientWidth - 32;
      setScale(Math.min(1, avail / CARD_W));
    };
    update();
    const ro = new ResizeObserver(update);
    if (containerRef.current) ro.observe(containerRef.current);
    return () => ro.disconnect();
  }, []);

  const [downloading, setDownloading] = useState<'png' | 'pdf' | null>(null);
  const [pickerFormat, setPickerFormat] = useState<'png' | 'pdf' | null>(null);

  const empresaPreview = {
    empresa: empresaInfo.empresa,
    logo: empresaInfo.logo,
    descripcion_corta: card.descripcion || card.puesto || '',
    telefono: card.telefono || '',
    email: card.email || '',
    direccion: card.direccion || '',
  };
  const personaPreview = {
    nombre: card.nombre,
    puesto: card.puesto,
    email: card.email,
    telefono: card.telefono,
    direccion: card.direccion,
    descripcion: card.descripcion,
    redes: card.redes,
  };
  const sharedCardProps: InvitationDesignProps = {
    empresa: empresaPreview,
    persona: personaPreview,
    qrCitasUrl: card.mostrar_qr_citas ? qrUrl ?? undefined : undefined,
    qrCatalogoUrl: card.mostrar_qr_catalogo
      ? qrCatalogoUrl ?? undefined
      : undefined,
    reservasOn,
    palette: cardPalette,
  };

  const safeName = card.nombre.replace(/\s+/g, '-').toLowerCase();

  const captureRef = async (
    ref: React.RefObject<HTMLDivElement>,
    bg: string
  ) => {
    if (!ref.current) throw new Error('ref not ready');
    await waitForImages(ref.current);

    const imgs = Array.from(ref.current.querySelectorAll('img'));
    const origSrcs: string[] = [];

    await Promise.all(
      imgs.map(async (img, i) => {
        origSrcs[i] = img.src;
        try {
          if (img.src && !img.src.startsWith('data:')) {
            img.src = await toDataUrl(img.src);
          }
        } catch {
          // fallback: mantener src original
        }
      })
    );

    const dataUrl = await toPng(ref.current, {
      pixelRatio: 3,
      backgroundColor: bg,
      cacheBust: true,
    });

    imgs.forEach((img, i) => {
      img.src = origSrcs[i];
    });

    return dataUrl;
  };

  const doDownloadPng = async (sizeId: CardSizeId) => {
    try {
      setDownloading('png');
      const size = CARD_SIZES.find((s) => s.id === sizeId)!;
      const naturalH = Math.round(size.widthMm * (CARD_H / CARD_W) * 10) / 10;
      const label = `${size.widthMm}x${naturalH}mm`;

      const frontUrl = await captureRef(frontRef, '#ffffff');
      const backUrl = await captureRef(backRef, cardPalette.primary);

      const dl = (url: string, suffix: string) => {
        const a = document.createElement('a');
        a.href = url;
        a.download = `tarjeta-${safeName}-${suffix}-${label}.png`;
        a.click();
      };
      dl(frontUrl, 'cara');
      await new Promise((r) => setTimeout(r, 200));
      dl(backUrl, 'dorso');
    } catch (e) {
      console.error('Error PNG', e);
    } finally {
      setDownloading(null);
      setPickerFormat(null);
    }
  };

  const doDownloadPdf = async (sizeId: CardSizeId) => {
    try {
      setDownloading('pdf');
      const size = CARD_SIZES.find((s) => s.id === sizeId)!;
      const naturalHeightMm = size.widthMm * (CARD_H / CARD_W);
      const yOffset = Math.max(0, (size.heightMm - naturalHeightMm) / 2);
      const cardHeightMm = Math.min(naturalHeightMm, size.heightMm);
      const orientation: 'landscape' | 'portrait' =
        size.widthMm >= size.heightMm ? 'landscape' : 'portrait';

      const frontUrl = await captureRef(frontRef, '#ffffff');
      const backUrl = await captureRef(backRef, cardPalette.primary);

      const pdf = new jsPDF({
        orientation,
        unit: 'mm',
        format: [size.widthMm, size.heightMm],
      });
      pdf.addImage(frontUrl, 'PNG', 0, yOffset, size.widthMm, cardHeightMm);
      pdf.addPage([size.widthMm, size.heightMm], orientation);
      pdf.addImage(backUrl, 'PNG', 0, yOffset, size.widthMm, cardHeightMm);
      pdf.save(`tarjeta-${safeName}-${size.widthMm}x${size.heightMm}mm.pdf`);
    } catch (e) {
      console.error('Error PDF', e);
    } finally {
      setDownloading(null);
      setPickerFormat(null);
    }
  };

  const handleConfirmSize = (sizeId: CardSizeId) => {
    if (pickerFormat === 'png') doDownloadPng(sizeId);
    else if (pickerFormat === 'pdf') doDownloadPdf(sizeId);
  };

  const cardDisplayH = CARD_H * scale;

  return (
    <>
      <SizePickerDialog
        open={!!pickerFormat}
        format={pickerFormat}
        onConfirm={handleConfirmSize}
        onClose={() => setPickerFormat(null)}
      />

      <div className="rounded-xl border border-gray-100 dark:border-white/10 bg-gray-50 dark:bg-white/5 overflow-hidden">
        {/* Cabecera */}
        <div className="flex items-center gap-4 px-4 py-3 bg-white dark:bg-white/5 border-b border-gray-100 dark:border-white/10">
          <div
            className="w-7 h-7 rounded-full shrink-0 border-2 border-white dark:border-white/10 shadow-sm"
            style={{ background: cardPalette.primary }}
          />
          <div className="flex-1 min-w-0">
            <p className="text-sm font-semibold text-gray-900 dark:text-white truncate">
              {card.nombre}
            </p>
            {card.puesto && (
              <p className="text-xs text-gray-400 dark:text-gray-500 truncate">
                {card.puesto}
              </p>
            )}
          </div>
          <div
            className={`flex items-center gap-1 shrink-0 ${
              isFirst ? 'tour-card-actions' : ''
            }`}
          >
            <button
              onClick={() => setPickerFormat('png')}
              disabled={!!downloading}
              className="p-1.5 rounded-lg text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 hover:bg-gray-100 dark:hover:bg-white/10 transition-colors disabled:opacity-40"
              aria-label="Descargar PNG"
              title="Descargar PNG"
            >
              {downloading === 'png' ? (
                <div className="w-4 h-4 border-2 border-gray-300 border-t-gray-600 rounded-full animate-spin" />
              ) : (
                <FileImage className="w-4 h-4" />
              )}
            </button>
            <button
              onClick={() => setPickerFormat('pdf')}
              disabled={!!downloading}
              className="p-1.5 rounded-lg text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 hover:bg-gray-100 dark:hover:bg-white/10 transition-colors disabled:opacity-40"
              aria-label="Descargar PDF"
              title="Descargar PDF"
            >
              {downloading === 'pdf' ? (
                <div className="w-4 h-4 border-2 border-gray-300 border-t-gray-600 rounded-full animate-spin" />
              ) : (
                <FileText className="w-4 h-4" />
              )}
            </button>
            <button
              onClick={onEdit}
              className="p-1.5 rounded-lg text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 hover:bg-gray-100 dark:hover:bg-white/10 transition-colors"
              aria-label="Editar"
            >
              <Pencil className="w-4 h-4" />
            </button>
            <button
              onClick={onDelete}
              className="p-1.5 rounded-lg text-gray-300 hover:text-red-500 hover:bg-red-50 dark:hover:bg-red-900/20 transition-colors"
              aria-label="Eliminar"
            >
              <Trash2 className="w-4 h-4" />
            </button>
          </div>
        </div>

        {/* Preview */}
        <div
          ref={containerRef}
          className="p-4 bg-gray-50 dark:bg-white/5 flex flex-col gap-3"
        >
          <div
            style={{
              width: CARD_W * scale,
              height: cardDisplayH,
              overflow: 'hidden',
              flexShrink: 0,
            }}
          >
            <div
              ref={frontRef}
              style={{
                width: CARD_W,
                transformOrigin: 'top left',
                transform: `scale(${scale})`,
              }}
            >
              <FrontCard {...sharedCardProps} />
            </div>
          </div>

          <div
            style={{
              width: CARD_W * scale,
              height: cardDisplayH,
              overflow: 'hidden',
              flexShrink: 0,
            }}
          >
            <div
              ref={backRef}
              style={{
                width: CARD_W,
                transformOrigin: 'top left',
                transform: `scale(${scale})`,
              }}
            >
              <BackCard {...sharedCardProps} />
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

/* ── BrandingAndLinks (principal) ───────────────────────────────── */

const BrandingAndLinks = () => {
  const { t } = useTranslation();
  const id_empresa = localStorage.getItem('id_empresa') || undefined;
  const { saveEmpresa } = useEmpresa(id_empresa);

  const {
    qrUrl,
    qrCatalogoUrl,
    bookingUrl,
    catalogoUrl,
    reservasOn,
    loading,
    generating,
    savingColors,
    empresaLoaded,
    empresaInfo,
    canvasCitasRef,
    canvasCatalogoRef,
    generateQR,
    toggleReservas,
    removeQR,
    downloadQR,
    copyLink,
    saveColors: saveColorsDB,
  } = useGenerateQR();

  const {
    cards,
    loading: loadingCards,
    saving: savingCard,
    deleteCard,
    insertCard,
    updateCard,
    fetchCards,
  } = useBusinessCards(id_empresa);

  const [runTour, setRunTour] = useState(false);
  usePageHelp(() => setRunTour(true), !loading);
  useTourVisibility(runTour);
  const [openDescDialog, setOpenDescDialog] = useState(false);
  const [openCardDialog, setOpenCardDialog] = useState(false);
  const [selectedCard, setSelectedCard] = useState<BusinessCard | null>(null);
  const steps: Step[] = GenerateQRTourSteps;

  const [primaryColor, setPrimaryColor] = useState(() =>
    getStoredColor(PRIMARY_COLOR_KEY, '#e63950')
  );
  const [secondaryColor, setSecondaryColor] = useState(() =>
    getStoredColor(SECONDARY_COLOR_KEY, '#103254')
  );
  const [primaryHex, setPrimaryHex] = useState(primaryColor);
  const [secondaryHex, setSecondaryHex] = useState(secondaryColor);

  useEffect(() => {
    if (empresaInfo?.color_primario) {
      setPrimaryColor(empresaInfo.color_primario);
      setPrimaryHex(empresaInfo.color_primario);
    }
    if (empresaInfo?.color_secundario) {
      setSecondaryColor(empresaInfo.color_secundario);
      setSecondaryHex(empresaInfo.color_secundario);
    }
  }, [empresaInfo]);

  const [descripcionCorta, setDescripcionCorta] = useState('');
  useEffect(() => {
    if (empresaInfo?.descripcion_corta)
      setDescripcionCorta(empresaInfo.descripcion_corta);
  }, [empresaInfo]);

  const [palette] = useState<ZigZagPalette>(() => {
    if (typeof window === 'undefined') return zigzagPalettes[0];
    try {
      const s = localStorage.getItem(PALETTE_STORAGE_KEY);
      return s ? JSON.parse(s) : zigzagPalettes[0];
    } catch {
      return zigzagPalettes[0];
    }
  });
  useEffect(() => {
    localStorage.setItem(PALETTE_STORAGE_KEY, JSON.stringify(palette));
  }, [palette]);

  /* ── Paginación de tarjetas ─────────────────────────────────────── */
  const [cardsPage, setCardsPage] = useState(1);
  const [cardsPageSize, setCardsPageSize] = useState(readStoredCardsPageSize);
  const totalCardPages = Math.max(1, Math.ceil(cards.length / cardsPageSize));
  useEffect(() => {
    if (cardsPage > totalCardPages) setCardsPage(totalCardPages);
  }, [cardsPage, totalCardPages]);
  const paginatedCards = cards.slice(
    (cardsPage - 1) * cardsPageSize,
    cardsPage * cardsPageSize
  );
  const handleCardsPageSizeChange = (size: number) => {
    setCardsPageSize(size);
    setCardsPage(1);
    if (typeof window !== 'undefined') {
      localStorage.setItem(CARDS_PAGE_SIZE_STORAGE_KEY, String(size));
    }
  };

  const handleOpenNewCard = () => {
    setSelectedCard(null);
    setOpenCardDialog(true);
  };
  const handleOpenEditCard = (card: BusinessCard) => {
    setSelectedCard(card);
    setOpenCardDialog(true);
  };
  const handleCloseCardDialog = (refresh?: boolean) => {
    setOpenCardDialog(false);
    setSelectedCard(null);
    if (refresh) fetchCards();
  };

  return (
    <motion.div
      className="w-full space-y-6 pb-28 lg:pb-0"
      initial="hidden"
      animate="visible"
      variants={containerVariants}
    >     
       {/* Header — el botón de ayuda vive ahora arriba a la derecha, fijo
          y global (HelpButton), lanzado desde usePageHelp. */}
      <motion.div variants={itemVariants} className="w-full">
        <div className="flex items-center gap-2 text-sm text-gray-600 dark:text-gray-400 tour-title">
          <span className="text-2xl font-bold tracking-tight text-gray-900 dark:text-white">
            Web, QR y tarjetas
          </span>
        </div>
      </motion.div>

      <Joyride
        steps={steps}
        run={runTour}
        styles={getTourStyles(isMobile)}
        continuous
        showSkipButton
        spotlightClicks
        locale={{
          back: t('profiles.tour.back'),
          close: t('profiles.tour.close'),
          last: t('profiles.tour.last'),
          next: t('profiles.tour.next'),
          skip: t('profiles.tour.skip'),
        }}
        callback={({ status }) =>
          ['finished', 'skipped'].includes(status) && setRunTour(false)
        }
      />

      {/* Colores web */}
      <motion.div variants={itemVariants}>
        <Card className={`rounded-2xl ${GLASS_CARD} tour-colors-card`}>
          <CardContent className="p-6 space-y-6">
            <div className="flex items-center gap-2 text-gray-600 dark:text-gray-400">
              <Palette className="w-5 h-5" />
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                Colores de tu página web
              </h3>
            </div>
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {[
                {
                  label: 'Color primario',
                  sub: 'Botones, CTAs, destacados',
                  id: 'primaryColorPicker',
                  color: primaryColor,
                  hex: primaryHex,
                  setColor: setPrimaryColor,
                  setHex: setPrimaryHex,
                  cls: 'tour-color-primary',
                },
                {
                  label: 'Color secundario',
                  sub: 'Fondos, textos, acentos',
                  id: 'secondaryColorPicker',
                  color: secondaryColor,
                  hex: secondaryHex,
                  setColor: setSecondaryColor,
                  setHex: setSecondaryHex,
                  cls: 'tour-color-secondary',
                },
              ].map(({ label, sub, id, color, hex, setColor, setHex, cls }) => (
                <div
                  key={id}
                  className={`rounded-lg border border-gray-200 dark:border-white/10 bg-gray-50 dark:bg-white/5 p-5 space-y-4 ${cls}`}
                >
                  <div>
                    <p className="font-medium text-gray-800 dark:text-white">
                      {label}
                    </p>
                    <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                      {sub}
                    </p>
                  </div>
                  <Label
                    htmlFor={id}
                    className="flex items-center gap-3 cursor-pointer w-fit group"
                  >
                    <div
                      className="w-9 h-9 rounded-full border-2 border-white dark:border-white/10 shadow-md group-hover:scale-105 transition-transform relative overflow-hidden"
                      style={{ backgroundColor: color }}
                    >
                      <input
                        id={id}
                        type="color"
                        value={color}
                        onChange={(e) => setColor(e.target.value)}
                        className="absolute inset-0 opacity-0 w-full h-full cursor-pointer"
                      />
                    </div>
                    <span className="text-sm text-gray-600 dark:text-gray-400 group-hover:text-gray-900 dark:group-hover:text-white transition-colors">
                      Pulsa para elegir color
                    </span>
                  </Label>
                  <div className="flex items-center gap-2">
                    <Input
                      value={hex}
                      onChange={(e) => {
                        setHex(e.target.value);
                        if (isValidHex(e.target.value))
                          setColor(e.target.value);
                      }}
                      maxLength={7}
                      className="w-32 font-mono text-sm dark:bg-white/5 dark:border-white/10 dark:text-white"
                      placeholder="#000000"
                    />
                    {!isValidHex(hex) && (
                      <span className="text-xs text-red-500">HEX inválido</span>
                    )}
                  </div>
                  <div className="flex flex-wrap gap-2">
                    {COLOR_PALETTE.map((c) => (
                      <button
                        key={c}
                        onClick={() => setColor(c)}
                        title={c}
                        className={`w-7 h-7 rounded-full border-2 transition-transform hover:scale-110 ${
                          color === c
                            ? 'border-gray-900 dark:border-white scale-110 shadow-md'
                            : 'border-transparent'
                        }`}
                        style={{ backgroundColor: c }}
                      />
                    ))}
                  </div>
                </div>
              ))}
            </div>
            <div className="flex flex-col sm:flex-row items-center gap-4 pt-1 tour-colors-save">
              <a
                href={catalogoUrl || undefined}
                target="_blank"
                rel="noopener noreferrer"
                className={`w-full sm:w-3/4 h-12 rounded-lg flex items-center justify-center text-white font-semibold text-sm tracking-wide shadow-sm transition-opacity ${
                  catalogoUrl
                    ? 'hover:opacity-80 cursor-pointer'
                    : 'pointer-events-none opacity-60'
                }`}
                style={{
                  background: `linear-gradient(135deg, ${primaryColor} 0%, ${secondaryColor} 100%)`,
                }}
              >
                Vista previa de tu web
              </a>
              <Button
                onClick={async () => {
                  localStorage.setItem(PRIMARY_COLOR_KEY, primaryColor);
                  localStorage.setItem(SECONDARY_COLOR_KEY, secondaryColor);
                  await saveColorsDB(primaryColor, secondaryColor);
                }}
                disabled={savingColors}
                className="w-full sm:w-1/4 bg-[#103254] hover:bg-[#0d2742] text-white disabled:opacity-60 rounded-lg transition-colors"
              >
                {savingColors ? 'Guardando...' : 'Guardar colores'}
              </Button>
            </div>
          </CardContent>
        </Card>
      </motion.div>

      {/* QR Bloques */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {/* QR Catálogo */}
        <motion.div
          custom={0}
          variants={fadeUp}
          initial="hidden"
          animate="visible"
          className={`tour-qr-catalogo relative flex flex-col gap-5 rounded-2xl p-6 ${GLASS_CARD} hover:shadow-md transition-shadow duration-300`}
        >
          {qrCatalogoUrl && (
            <button
              onClick={() => removeQR('catalogo')}
              className="absolute top-4 right-4 p-1.5 rounded-lg text-gray-300 hover:text-red-500 hover:bg-red-50 dark:hover:bg-red-900/20 transition-colors"
            >
              <Trash2 className="w-4 h-4" />
            </button>
          )}
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 rounded-xl bg-gray-50 dark:bg-white/5 border border-gray-100 dark:border-white/10 flex items-center justify-center shrink-0">
              <ExternalLink className="w-4 h-4 text-gray-500 dark:text-gray-400" />
            </div>
            <h3 className="text-sm font-semibold text-gray-900 dark:text-white">
              {t('generateQR.qrCatalogTitle')}
            </h3>
          </div>
          <div className="flex flex-col items-center gap-4 flex-1 justify-center py-2 tour-qr-catalogo-actions">
            {loading ? (
              <div className="w-40 h-40 rounded-2xl bg-gray-50 dark:bg-white/5 flex items-center justify-center">
                <div className="w-6 h-6 border-2 border-gray-300 border-t-gray-700 rounded-full animate-spin" />
              </div>
            ) : qrCatalogoUrl ? (
              <>
                <div className="relative group">
                  <div className="absolute inset-0 rounded-2xl bg-gradient-to-br from-gray-200 to-gray-100 dark:from-white/10 dark:to-white/5 translate-y-1.5 translate-x-1.5 group-hover:translate-y-2 group-hover:translate-x-2 transition-transform duration-300" />
                  <div className="relative bg-white dark:bg-[#1c1c1e] rounded-2xl p-4 border border-gray-100 dark:border-white/10">
                    <img
                      src={qrCatalogoUrl}
                      alt={t('generateQR.qrCatalogTitle')}
                      className="w-40 h-40 block"
                    />
                  </div>
                </div>
                {catalogoUrl && (
                  <p className="text-xs text-gray-400 dark:text-gray-500 font-mono truncate max-w-xs text-center">
                    {catalogoUrl}
                  </p>
                )}
              </>
            ) : (
              <>
                <canvas ref={canvasCatalogoRef} className="hidden" />
                <div className="flex flex-col items-center gap-5">
                  <div className="w-40 h-40 rounded-2xl border-2 border-dashed border-gray-200 dark:border-white/10 flex items-center justify-center bg-gray-50 dark:bg-white/5">
                    <QrCode className="w-10 h-10 text-gray-300 dark:text-gray-600" />
                  </div>
                  <button
                    onClick={() => generateQR('catalogo')}
                    disabled={generating}
                    className="inline-flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium bg-gray-900 dark:bg-white text-white dark:text-gray-900 hover:bg-gray-700 dark:hover:bg-gray-100 transition-all duration-200 disabled:opacity-40 disabled:cursor-not-allowed"
                  >
                    <Sparkles className="w-4 h-4" />
                    {generating
                      ? t('generateQR.generating')
                      : t('generateQR.generateCatalog')}
                  </button>
                </div>
              </>
            )}
          </div>
          {qrCatalogoUrl && (
            <div className="flex gap-2 flex-wrap">
              <button
                onClick={() => downloadQR(qrCatalogoUrl)}
                className="inline-flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium bg-gray-900 dark:bg-white text-white dark:text-gray-900 hover:bg-gray-700 dark:hover:bg-gray-100 transition-all duration-200"
              >
                <Download className="w-4 h-4" />
                {t('generateQR.downloadQR')}
              </button>
              <button
                onClick={() => copyLink(catalogoUrl)}
                className="inline-flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium border border-gray-200 dark:border-white/10 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-white/10 transition-all duration-200"
              >
                <Copy className="w-4 h-4" />
                {t('generateQR.copyLink')}
              </button>
            </div>
          )}
        </motion.div>

        {/* QR Citas */}
        {empresaLoaded && (
          <motion.div
            custom={1}
            variants={fadeUp}
            initial="hidden"
            animate="visible"
            className={`tour-qr-citas relative flex flex-col gap-5 rounded-2xl p-6 ${GLASS_CARD} hover:shadow-md transition-shadow duration-300`}
          >
            {qrUrl && (
              <button
                onClick={() => removeQR('citas')}
                className="absolute top-4 right-4 p-1.5 rounded-lg text-gray-300 hover:text-red-500 hover:bg-red-50 dark:hover:bg-red-900/20 transition-colors"
              >
                <Trash2 className="w-4 h-4" />
              </button>
            )}
            <div className="flex items-start gap-3">
              <div className="w-9 h-9 rounded-xl bg-gray-50 dark:bg-white/5 border border-gray-100 dark:border-white/10 flex items-center justify-center shrink-0">
                <Calendar className="w-4 h-4 text-gray-500 dark:text-gray-400" />
              </div>
              <div className="flex flex-col gap-1.5">
                <h3 className="text-sm font-semibold text-gray-900 dark:text-white">
                  {t('generateQR.qrBookingTitle')}
                </h3>
                <StatusPill
                  active={reservasOn}
                  labelOn={t('generateQR.bookingsOn')}
                  labelOff={t('generateQR.bookingsOff')}
                />
              </div>
            </div>
            <div className="flex items-center gap-3 tour-switch-reservas">
              <span className="text-xs font-medium text-gray-500 dark:text-gray-400">
                {t('generateQR.onlineBookings')}
              </span>
              <Switch checked={reservasOn} onCheckedChange={toggleReservas} />
            </div>
            <div className="flex flex-col items-center gap-4 flex-1 justify-center py-2 tour-qr-citas-actions">
              {loading ? (
                <div className="w-40 h-40 rounded-2xl bg-gray-50 dark:bg-white/5 flex items-center justify-center">
                  <div className="w-6 h-6 border-2 border-gray-300 border-t-gray-700 rounded-full animate-spin" />
                </div>
              ) : qrUrl ? (
                <>
                  <div className="relative group">
                    <div className="absolute inset-0 rounded-2xl bg-gradient-to-br from-gray-200 to-gray-100 dark:from-white/10 dark:to-white/5 translate-y-1.5 translate-x-1.5 group-hover:translate-y-2 group-hover:translate-x-2 transition-transform duration-300" />
                    <div className="relative bg-white dark:bg-[#1c1c1e] rounded-2xl p-4 border border-gray-100 dark:border-white/10">
                      <img
                        src={qrUrl}
                        alt={t('generateQR.qrBookingTitle')}
                        className="w-40 h-40 block"
                      />
                    </div>
                  </div>
                  {bookingUrl && (
                    <p className="text-xs text-gray-400 dark:text-gray-500 font-mono truncate max-w-xs text-center">
                      {bookingUrl}
                    </p>
                  )}
                </>
              ) : (
                <>
                  <canvas ref={canvasCitasRef} className="hidden" />
                  <div className="flex flex-col items-center gap-5">
                    <div className="w-40 h-40 rounded-2xl border-2 border-dashed border-gray-200 dark:border-white/10 flex items-center justify-center bg-gray-50 dark:bg-white/5">
                      <QrCode className="w-10 h-10 text-gray-300 dark:text-gray-600" />
                    </div>
                    <button
                      onClick={() => generateQR('citas')}
                      disabled={generating}
                      className="inline-flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium bg-gray-900 dark:bg-white text-white dark:text-gray-900 hover:bg-gray-700 dark:hover:bg-gray-100 transition-all duration-200 disabled:opacity-40 disabled:cursor-not-allowed"
                    >
                      <Sparkles className="w-4 h-4" />
                      {generating
                        ? t('generateQR.generating')
                        : t('generateQR.generateBooking')}
                    </button>
                  </div>
                </>
              )}
            </div>
            {qrUrl && (
              <div className="flex gap-2 flex-wrap">
                <button
                  onClick={() => downloadQR(qrUrl)}
                  className="inline-flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium bg-gray-900 dark:bg-white text-white dark:text-gray-900 hover:bg-gray-700 dark:hover:bg-gray-100 transition-all duration-200"
                >
                  <Download className="w-4 h-4" />
                  {t('generateQR.downloadQR')}
                </button>
                <button
                  onClick={() => copyLink(bookingUrl)}
                  className="inline-flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium border border-gray-200 dark:border-white/10 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-white/10 transition-all duration-200"
                >
                  <Copy className="w-4 h-4" />
                  {t('generateQR.copyLink')}
                </button>
              </div>
            )}
          </motion.div>
        )}
      </div>

      {/* Tarjetas de presentación */}
      {empresaLoaded && (
        <motion.div
          custom={2}
          variants={fadeUp}
          initial="hidden"
          animate="visible"
        >
          <Card className={`rounded-2xl ${GLASS_CARD} overflow-hidden tour-cards-section`}>
            <CardContent className="p-6 space-y-5">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2 text-gray-600 dark:text-gray-400">
                  <CreditCard className="w-5 h-5" />
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                      Tarjetas de presentación
                    </h3>
                    <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                      Crea una tarjeta por persona o trabajador
                    </p>
                  </div>
                </div>
                <button
                  onClick={handleOpenNewCard}
                  className="tour-cards-new-btn inline-flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium bg-gray-900 dark:bg-white text-white dark:text-gray-900 hover:bg-gray-700 dark:hover:bg-gray-100 transition-all duration-200"
                >
                  <Plus className="w-4 h-4" />
                  <span className="hidden sm:inline">Nueva tarjeta</span>
                </button>
              </div>
              {loadingCards ? (
                <div className="flex justify-center py-8">
                  <div className="w-6 h-6 border-2 border-gray-300 border-t-gray-700 rounded-full animate-spin" />
                </div>
              ) : cards.length === 0 ? (
                <div className="flex flex-col items-center gap-3 py-10 text-center">
                  <div className="w-12 h-12 rounded-2xl border-2 border-dashed border-gray-200 dark:border-white/10 flex items-center justify-center">
                    <CreditCard className="w-5 h-5 text-gray-300 dark:text-gray-600" />
                  </div>
                  <p className="text-sm text-gray-400 dark:text-gray-500">
                    Aún no tienes tarjetas de presentación
                  </p>
                  <button
                    onClick={handleOpenNewCard}
                    className="text-sm font-medium text-[#103254] dark:text-blue-400 hover:underline"
                  >
                    Crear la primera
                  </button>
                </div>
              ) : (
                <>
                  <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4 tour-cards-list">
                    {paginatedCards.map((card, idx) => {
                      const cardPalette =
                        zigzagPalettes.find((p) => p.id === card.paleta) ??
                        zigzagPalettes[0];
                      return (
                        <CardRow
                          key={card.id_tarjeta}
                          card={card}
                          cardPalette={cardPalette}
                          empresaInfo={empresaInfo}
                          qrUrl={qrUrl}
                          qrCatalogoUrl={qrCatalogoUrl}
                          reservasOn={reservasOn}
                          isFirst={cardsPage === 1 && idx === 0}
                          onEdit={() => handleOpenEditCard(card)}
                          onDelete={() => deleteCard(card.id_tarjeta!)}
                        />
                      );
                    })}
                  </div>

                  <Pagination
                    page={cardsPage}
                    totalPages={totalCardPages}
                    totalItems={cards.length}
                    pageSize={cardsPageSize}
                    onPageChange={setCardsPage}
                    onPageSizeChange={handleCardsPageSizeChange}
                    itemLabel="tarjetas"
                    className="tour-cards-pagination"
                    pageSizeOptions={CARDS_PAGE_SIZE_OPTIONS}
                  />
                </>
              )}
            </CardContent>
          </Card>
        </motion.div>
      )}

      {/* Dialog descripción corta (bottom sheet) */}
      <Dialog open={openDescDialog} onOpenChange={setOpenDescDialog}>
        <DialogContent
          className="
            z-[60] p-0 border border-gray-200 dark:border-white/10 bg-white dark:bg-[#1c1c1e]
            [&>button:last-child]:hidden
            flex flex-col
            !bottom-0 !left-0 !top-auto !translate-x-0 !translate-y-0
            w-full max-w-none max-h-[85vh] rounded-t-2xl rounded-b-none
            data-[state=open]:slide-in-from-bottom data-[state=closed]:slide-out-to-bottom
            sm:!bottom-auto sm:!left-1/2 sm:!top-1/2 sm:!-translate-x-1/2 sm:!-translate-y-1/2
            sm:w-[95%] sm:max-w-lg sm:mx-auto sm:h-auto sm:max-h-[85vh] sm:rounded-xl sm:shadow-xl
          "
        >
          <div className="px-4 sm:px-6 pt-5 pb-3 shrink-0 relative">
            <DialogHeader>
              <DialogTitle className="text-lg sm:text-xl font-semibold text-[#103254] dark:text-blue-400 pr-10">
                Descripción de la tarjeta
              </DialogTitle>
              <DialogDescription className="text-xs text-gray-500 dark:text-gray-400">
                Texto breve que aparece en la tarjeta de presentación de tu
                negocio.
              </DialogDescription>
            </DialogHeader>
            <button
              onClick={() => setOpenDescDialog(false)}
              className="absolute top-4 right-4 w-9 h-9 flex items-center justify-center rounded-full text-gray-500 hover:text-gray-800 dark:hover:text-gray-100 hover:bg-gray-100 dark:hover:bg-white/10 transition-colors"
              aria-label="Cerrar"
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          <div className="flex-1 overflow-y-auto px-4 sm:px-6 pb-5">
            <div className="space-y-3 rounded-xl border border-gray-200 dark:border-white/10 bg-white dark:bg-white/5 shadow-sm p-4">
              <label className="text-sm font-medium text-gray-700 dark:text-gray-300">
                Descripción corta
              </label>
              <Textarea
                value={descripcionCorta}
                onChange={(e) => setDescripcionCorta(e.target.value)}
                maxLength={100}
                placeholder="Breve descripción (máx. 100 caracteres)"
                className="resize-none dark:bg-white/5 dark:border-white/10 dark:text-white"
                rows={3}
              />
              <p className="text-xs text-gray-400 text-right">
                {descripcionCorta.length}/100
              </p>
            </div>
          </div>

          <DialogFooter className="px-4 sm:px-6 pt-2 pb-5 sm:pb-4 flex flex-col sm:flex-row gap-3 shrink-0 border-t border-gray-100 dark:border-white/10">
            <Button
              variant="outline"
              onClick={() => setOpenDescDialog(false)}
              className={`w-full sm:flex-1 ${DLG_BTN_SECONDARY}`}
            >
              Cancelar
            </Button>
            <Button
              className={`w-full sm:flex-1 ${DLG_BTN_PRIMARY}`}
              onClick={async () => {
                await saveEmpresa({ descripcion_corta: descripcionCorta });
                setOpenDescDialog(false);
              }}
            >
              Guardar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Dialog tarjeta */}
      {id_empresa && empresaInfo && (
        <BusinessCardDialog
          open={openCardDialog}
          onClose={handleCloseCardDialog}
          id_empresa={id_empresa}
          empresaInfo={empresaInfo}
          card={selectedCard}
          qrCitasUrl={qrUrl}
          qrCatalogoUrl={qrCatalogoUrl}
          reservasOn={reservasOn}
          saving={savingCard}
          insertCard={insertCard}
          updateCard={updateCard}
        />
      )}
    </motion.div>
  );
};

export default BrandingAndLinks;